/* eslint-disable no-restricted-globals */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Center from '../../assets/styles/Center';
import FormContainer from '../../assets/styles/FormContainer';
import FormAuth from '../../components/FormAuth';
import GroupFile from '../../components/GroupFile';
import GroupInput from '../../components/GroupInput';
import Loader from '../../components/Loader';
import Title from '../../components/Title';
import { useUpdateClient } from '../../hooks/mutations/useClientMutations';
import { useCities } from '../../hooks/queries/useCities';
import { useClientById } from '../../hooks/queries/useClients';
import useErrors from '../../hooks/useErrors';
import ClientsService from '../../services/ClientsService';
import uploadS3 from '../../services/utils/uploadS3';
import { useStore } from '../../stores';
import calculateAge from '../../utils/calculateAge';

export default function ClientDescription() {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [select, setSelect] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(false);
  const [preview, setPreview] = useState({ profile_photo: null });

  const { id } = useStore((state) => state.user);
  const { setError, removeError, getErrorMessageByFieldName } = useErrors();

  // Mutation para atualizar cliente
  const { mutate: updateClient, isPending: isUpdating } = useUpdateClient();

  // Fetch cities
  const {
    data: citys = [],
    isLoading: loadingCity,
    isError: citiesError,
  } = useCities();

  // Fetch client data
  const {
    data: client,
    isLoading: loadingClient,
    isError: clientError,
  } = useClientById(id);

  // Format date helper function
  function formatDate(dateString) {
    if (!dateString) return '';

    if (
      typeof dateString === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ) {
      return dateString;
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    // Formata para yyyy-MM-dd
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Load client data into form when available
  useEffect(() => {
    if (client) {
      setName(client.name || '');
      const formattedDate = formatDate(client.dateOfBirth);
      setDateOfBirth(formattedDate);
      setDescription(client.description || '');
      setSelect(client.city?.id || '');

      console.log(client.profilePhoto);
      if (client.profilePhoto) {
        setPreview({ profile_photo: client.profilePhoto });
      }
    }
  }, [client]);

  // Show error toasts
  useEffect(() => {
    if (citiesError) {
      toast.error('Erro ao carregar cidades');
    }
    if (clientError) {
      toast.error('Erro ao carregar dados do cliente');
    }
  }, [citiesError, clientError]);

  function handleName(e) {
    setName(e.target.value);
  }

  function handleDateOfBirthChange(e) {
    setDateOfBirth(e.target.value);

    removeError('date');
  }

  function handleDescription(e) {
    setDescription(e.target.value);
  }

  function handleSelect(e) {
    setSelect(e.target.value);
  }

  const validateFile = (file) => {
    if (!file) return false;

    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (file.size > maxSize) {
      toast.error('Imagem deve ter menos de 5MB');
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido. Use JPEG, PNG ou WebP');
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setProfilePhoto(null);
      setPreview({ profile_photo: null });
      return;
    }

    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }

    // Cria preview
    try {
      const previewUrl = URL.createObjectURL(file);
      setProfilePhoto(file);
      setPreview({ profile_photo: previewUrl });
    } catch (error) {
      console.error('Erro ao criar preview:', error);
      toast.error('Erro ao processar imagem');
    }
  };

  const validateAge = (date) => {
    const age = calculateAge(date);
    if (age < 18) {
      setError({
        field: 'date',
        message: 'É necessário ter pelo menos 18 anos',
      });
      return false;
    }
    return true;
  };

  async function onSubmit() {
    try {
      setLoading(true);

      if (!validateAge(dateOfBirth)) {
        return null;
      }

      if (!name || !select) {
        throw new Error('Os campos estão vázios');
      }

      // Verifica se precisa de foto: só é obrigatório se não tiver foto existente
      if (!client?.profilePhoto && !profilePhoto) {
        toast.error('Foto de perfil é obrigatória');
        return null;
      }

      let photoKey = null;

      // Se o usuário selecionou uma nova foto, faz o upload
      if (profilePhoto) {
        const { url, key } = await ClientsService.getPresignedProfile(
          profilePhoto
        );
        await uploadS3(url, profilePhoto);
        photoKey = key; // Atualiza com a nova foto
      }

      const body = {
        id,
        name,
        dateOfBirth,
        description,
        cityId: select,
        profilePhoto: photoKey,
      };

      // Usa mutation para atualizar e invalidar cache automaticamente
      updateClient(body);
    } catch (error) {
      console.log(error);
      toast.error(error.error || 'Erro ao atualizar!');
    } finally {
      setLoading(false);
    }

    return null;
  }

  const isFormValid = Boolean(name && dateOfBirth);

  if (loadingCity || loadingClient) return <Loader isLoading />;

  return (
    <FormContainer>
      <Center className="center">
        <Title title="Descrição básica" subtitle="faça sua descrição básica" />

        <FormAuth
          onSubmit={onSubmit}
          buttonTitle="Continuar"
          isLoading={loading || isUpdating}
          twoButtons
          next
          formValid={isFormValid}
        >
          <GroupFile
            title="Foto de perfil"
            idForm="profile_photo"
            prev={preview}
            // prev={{ profile_photo: preview.profile_photo }}
          >
            <input
              type="file"
              name="file"
              id="profile_photo"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />
          </GroupFile>

          <GroupInput>
            <label>Nome*</label>
            <input
              type="text"
              placeholder="Digite seu nome"
              value={name}
              onChange={handleName}
            />
          </GroupInput>

          <GroupInput error={getErrorMessageByFieldName('date')}>
            <label>Data de nascimento*</label>
            <input
              type="date"
              placeholder="Digite sua idade"
              value={dateOfBirth}
              onChange={handleDateOfBirthChange}
              max={new Date().toISOString().split('T')[0]}
            />
          </GroupInput>

          <GroupInput>
            <label>Sobre você</label>
            <textarea
              type="text"
              placeholder="Digite sobre você"
              value={description}
              onChange={handleDescription}
            />
          </GroupInput>

          <GroupInput select={Boolean(select)}>
            <label>Sua cidade*</label>
            <select value={select} onChange={handleSelect}>
              {!loadingCity && (
                <>
                  <option value="" disabled selected>
                    Escolha uma opção
                  </option>

                  {citys?.map((city) => (
                    <option value={city.id} key={city.id}>
                      {city.name} -{city.stateAc}
                    </option>
                  ))}
                </>
              )}
            </select>
          </GroupInput>
        </FormAuth>
      </Center>
    </FormContainer>
  );
}
